/**
 * Manager API Client with Token Refresh
 * Provides fetchWithAuth for authenticated API calls
 * Adapted from hcp-admin-frontend/adminApi.ts
 */

import { authService } from './authService';

// API Base URL from environment (should include /api suffix)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// Get auth token from authService
const getAuthToken = (): string | null => {
  return authService.getAuthToken();
};

// Get refresh token from authService
const getRefreshToken = (): string | null => {
  return authService.getRefreshToken();
};

// Handle authentication failure - clear auth and redirect
const handleAuthFailure = () => {
  authService.clearAuth();
  window.location.href = '/login?session_expired=true';
};

// Track if token refresh is in progress to prevent multiple refreshes
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh access token using refresh token
 */
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log('No refresh token available');
    return null;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('Attempting to refresh access token...');
      const response = await fetch(`${API_BASE_URL}/stores/admin-refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          authService.updateAccessToken(data.access_token);
          console.log('Token refreshed successfully');
          return data.access_token;
        }
      }

      console.log('Token refresh failed');
      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Authenticated fetch wrapper with automatic token refresh
 */
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {},
  isRetry = false
): Promise<any> => {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - try to refresh token
  if (response.status === 401 && !isRetry) {
    console.log('Received 401, attempting token refresh...');
    const newToken = await refreshAccessToken();

    if (newToken) {
      // Retry the original request with the new token
      console.log('Retrying request with new token');
      return fetchWithAuth(url, options, true);
    } else {
      // Refresh failed, redirect to login
      handleAuthFailure();
      throw new Error('Authentication failed');
    }
  }

  // Handle other errors
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined;
  }

  return response.json();
};

/**
 * Multipart POST with the same refresh-and-retry as fetchWithAuth.
 *
 * Uploads can't go through fetchWithAuth because it sets
 * Content-Type: application/json, while multipart needs the browser to set the
 * header itself with a boundary. Without this wrapper, uploads become the ONLY
 * calls that fail on an expired token — everything else refreshes silently, so
 * the app never logs out and it looks as though uploading specifically is
 * broken. Video makes that likely: a multi-minute browser-side conversion is
 * dead time with no API traffic to trigger a refresh.
 */
export const fetchMultipartWithAuth = async (
  url: string,
  formData: FormData,
  isRetry = false
): Promise<any> => {
  const token = getAuthToken();

  const response = await fetch(url, {
    method: 'POST',
    // No Content-Type — the browser sets multipart/form-data with a boundary.
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (response.status === 401 && !isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return fetchMultipartWithAuth(url, formData, true);
    }
    handleAuthFailure();
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || `Upload failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * GET request with authentication
 */
export const get = async (endpoint: string): Promise<any> => {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`);
};

/**
 * POST request with authentication
 */
export const post = async (endpoint: string, data: any): Promise<any> => {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request with authentication
 */
export const put = async (endpoint: string, data: any): Promise<any> => {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * PATCH request with authentication
 */
export const patch = async (endpoint: string, data: any): Promise<any> => {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request with authentication
 */
export const del = async (endpoint: string): Promise<any> => {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
  });
};

const managerApi = {
  fetchWithAuth,
  fetchMultipartWithAuth,
  get,
  post,
  put,
  patch,
  del,
  getAuthToken,
  getRefreshToken,
};

export default managerApi;
